import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/format';
import { useCreateEstimate, useEstimates } from '@/routes/estimates/useEstimates';

/**
 * A job's estimates, and "New estimate" (M2-06) — where a technician starts
 * one: the job already names the customer and the property, and the API
 * requires a technician's estimate to be for a job they are on, which being
 * on this page means they are.
 */
export function JobEstimates({
  jobId,
  customerId,
  propertyId,
}: {
  jobId: string;
  customerId: string;
  propertyId: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const estimates = useEstimates({ jobId });
  const create = useCreateEstimate();

  return (
    <section aria-labelledby="job-estimates-heading" className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 id="job-estimates-heading" className="text-base font-semibold">
          {t('jobDetail.estimates.heading')}
        </h2>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={create.isPending}
          onClick={() =>
            create.mutate(
              { customerId, propertyId, jobId },
              { onSuccess: (estimate) => navigate(`/estimates/${estimate.id}`) },
            )
          }
        >
          {create.isPending ? t('jobDetail.estimates.creating') : t('jobDetail.estimates.new')}
        </Button>
      </div>
      {create.isError ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {t('jobDetail.estimates.failed')}
        </p>
      ) : null}
      {estimates.isPending ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {t('jobDetail.estimates.loading')}
        </p>
      ) : estimates.isError ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {t('jobDetail.estimates.loadError')}
        </p>
      ) : estimates.data.length === 0 ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {t('jobDetail.estimates.empty')}
        </p>
      ) : (
        <ul className="divide-y divide-[var(--color-border)] text-sm" data-testid="job-estimates">
          {estimates.data.map((e) => {
            const number = Number(e.number);
            const revision = Number(e.revision);
            return (
              <li key={e.id} className="flex items-baseline justify-between gap-3 py-2">
                <Link to={`/estimates/${e.id}`} className="font-medium hover:underline">
                  {revision > 1
                    ? t('jobDetail.estimates.rowRevision', { number, revision })
                    : t('jobDetail.estimates.row', { number })}
                </Link>
                <span className="text-[var(--color-muted-foreground)]">
                  {t(`estimates.status.${e.status}`, { defaultValue: e.status })}
                  {/* M2-09: its lines are on a job — this one, since it is listed here. */}
                  {e.convertedAt ? ` · ${t('jobDetail.estimates.converted')}` : null}
                </span>
                <span className="tabular-nums">{formatCurrency(Number(e.total))}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
